import { createContext } from '@synoptic/ui-kit/utils/create-context.js';
import Graph from 'graphology';
import { circlepack } from 'graphology-layout';
import { useLayoutEffect, useState } from 'react';
import { EdgeData, NodeDataMini } from '@/services/twttr-graph-service';

const [Provider, useCtx] = createContext<Graph>('GraphProvider');

const buildGraph = (nodes: NodeDataMini[]) => {
  const graph = new Graph();

  nodes.forEach((node) => {
    const score = node.score || 0;

    const size = Math.sqrt(score);

    graph.addNode(String(node.id), {
      label: node.screen_name || String(node.id),
      size,
      score,
      community: node.community,
    });
  });

  circlepack.assign(graph, {
    hierarchyAttributes: ['community', 'score'],
  });

  return graph;
};

const ensureEdges = (graph: Graph, edges: EdgeData[]) => {
  edges.forEach(([source, target]) => {
    const sourceStr = String(source);
    const targetStr = String(target);

    const edgeId = `${sourceStr}-${targetStr}`;
    if (!graph.hasEdge(sourceStr, targetStr)) {
      graph.addEdge(sourceStr, targetStr, {
        id: edgeId,
        size: 1,
        hidden: true,
      });
    }
  });
};

export const GraphProvider = ({
  nodes,
  edges,
  children,
}: {
  nodes: NodeDataMini[];
  edges: EdgeData[];
  children: React.ReactNode;
}) => {
  const [graph] = useState(() => buildGraph(nodes));

  // very dirty fix. using usLayoutEffect to ensure it runs before "useEffect" from children
  // otherwise we get a race condition and on node select graph can re-render before edges are set
  useLayoutEffect(() => {
    // not cool to re-iterate all edges but keeps code much simpler. Let's try to live like that for now
    ensureEdges(graph, edges);
  }, [edges, graph]);

  return <Provider value={graph}>{children}</Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useGraph = useCtx;
